import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

// Define interfaces needed for ProfileData
interface FinancialGoal {
  id: string;
  label: string;
  priority?: number;
  selected: boolean;
  custom?: boolean;
  customValue?: string;
}

interface Investment {
  id: string;
  category: string;
  name: string;
  value: string;
  purchaseDate: string;
  purpose: string;
}

// Define the shape of our profile data
export interface ProfileData {
  basicInfo: {
    name: string;
    age: number;
    income: number;
    dependents: number;
    skipped: boolean;
  };
  financialGoals: {
    goals: FinancialGoal[];
    skipped: boolean;
  };
  riskAssessment: {
    score: number;
    answers: Record<string, number>;
    skipped: boolean;
  };
  currentPortfolio: {
    investments: Investment[];
    skipped: boolean;
  };
  investmentPreferences: {
    preferredAssetClasses: string[];
    ethicalPreferences: string[];
    liquidityNeeds: number;
    investmentHorizon: number;
    skipped: boolean;
  };
}

// Context type
interface ProfileContextType {
  currentStep: number;
  setCurrentStep: (step: number) => void;
  profileData: ProfileData;
  updateProfileData: (section: string, data: any, showToast?: boolean) => void;
  isLoading: boolean;
  lastSaved: Date | null;
  setFlushStepDataFn: (fn: (() => Promise<void>) | null) => void;
  flushCurrentStepData: () => Promise<void>;
}

const ProfileContext = createContext<ProfileContextType | undefined>(undefined);

// Helper function to ensure data is an object
const ensureObject = (data: any): Record<string, any> => {
  if (typeof data === 'object' && data !== null) {
    return data;
  }
  return {};
};

// Provider component
export const ProfileProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [profileData, setProfileData] = useState<ProfileData>({
    basicInfo: {
      name: '',
      age: 30,
      income: 100000,
      dependents: 0,
      skipped: false,
    },
    financialGoals: {
      goals: [],
      skipped: false,
    },
    riskAssessment: {
      score: 0,
      answers: {},
      skipped: false,
    },
    currentPortfolio: {
      investments: [],
      skipped: false,
    },
    investmentPreferences: {
      preferredAssetClasses: [],
      ethicalPreferences: [],
      liquidityNeeds: 5,
      investmentHorizon: 10,
      skipped: false,
    },
  });
  const [profileId, setProfileId] = useState<string | null>(null);
  const [flushStepDataFn, setFlushStepDataFn] = useState<(() => Promise<void>) | null>(null);

  // Load profile data from Supabase
  useEffect(() => {
    const loadProfileData = async () => {
      if (!user) {
        setIsLoading(false);
        return;
      }

      try {
        // Try to get the current profile for the user
        const { data, error } = await supabase
          .from('investor_profiles')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();

        if (error) {
          throw error;
        }

        if (!data) {
          // No profile found, create one
          const { data: insertData, error: insertError } = await supabase
            .from('investor_profiles')
            .insert([{ user_id: user.id }])
            .select('*')
            .single();

          if (insertError) {
            throw insertError;
          }
          setProfileId(insertData.id);
          setProfileData({
            basicInfo: {
              name: '',
              age: 30,
              income: 100000,
              dependents: 0,
              skipped: false,
            },
            financialGoals: {
              goals: [],
              skipped: false,
            },
            riskAssessment: {
              score: 0,
              answers: {},
              skipped: false,
            },
            currentPortfolio: {
              investments: [],
              skipped: false,
            },
            investmentPreferences: {
              preferredAssetClasses: [],
              ethicalPreferences: [],
              liquidityNeeds: 5,
              investmentHorizon: 10,
              skipped: false,
            },
          });
          setLastSaved(insertData.updated_at ? new Date(insertData.updated_at) : null);
        } else {
          setProfileId(data.id);
          setProfileData({
            basicInfo: {
              name: data.basic_info.name,
              age: data.basic_info.age,
              income: data.basic_info.income,
              dependents: data.basic_info.dependents,
              skipped: data.basic_info.skipped,
            },
            financialGoals: {
              goals: data.financial_goals.goals,
              skipped: data.financial_goals.skipped,
            },
            riskAssessment: {
              score: data.risk_assessment.score,
              answers: data.risk_assessment.answers,
              skipped: data.risk_assessment.skipped,
            },
            currentPortfolio: {
              investments: data.current_portfolio.investments,
              skipped: data.current_portfolio.skipped,
            },
            investmentPreferences: {
              preferredAssetClasses: data.investment_preferences.preferred_asset_classes,
              ethicalPreferences: data.investment_preferences.ethical_preferences,
              liquidityNeeds: data.investment_preferences.liquidity_needs,
              investmentHorizon: data.investment_preferences.investment_horizon,
              skipped: data.investment_preferences.skipped,
            },
          });
          setLastSaved(data.updated_at ? new Date(data.updated_at) : null);
        }
      } catch (error: any) {
        console.error('Error loading profile:', error);
        toast.error('Failed to load profile data');
      } finally {
        setIsLoading(false);
      }
    };

    loadProfileData();
  }, [user]);

  const updateProfileData = async (section: string, data: any, showToast = false) => {
    if (!user || !profileId) {
      console.error("User or profileId not set, cannot update profile");
      return Promise.resolve(); // Resolve instead of reject to prevent cascading errors
    }

    // Make sure we're dealing with an object
    const processedData = ensureObject(data);

    const sectionMapping: Record<string, string> = {
      basicInfo: 'basic_info',
      financialGoals: 'financial_goals',
      riskAssessment: 'risk_assessment',
      currentPortfolio: 'current_portfolio',
      investmentPreferences: 'investment_preferences'
    };

    // Update local state immediately for responsiveness
    setProfileData(prev => ({
      ...prev,
      [section]: processedData
    }));

    console.log(`[ProfileContext] updateProfileData called for section: ${section}, data:`, processedData); // <-- LOG

    try {
      // *** Only update, never insert. Use the existing profile row. ***
      const { error } = await supabase
        .from('investor_profiles')
        .update({
          [sectionMapping[section]]: processedData
        })
        .eq('id', profileId);

      if (error) {
        console.error('Supabase update error:', error);
        if (showToast) {
          toast.error('Failed to save changes to server, but changes are saved locally');
        }
        return Promise.resolve(); // Still resolve the promise
      }
      
      setLastSaved(new Date());
      if (showToast) {
        toast.success('Profile updated successfully');
      }
      return Promise.resolve();
    } catch (error) {
      console.error('Error saving profile:', error);
      if (showToast) {
        toast.error('Failed to save changes to server, but changes are saved locally');
      }
      return Promise.resolve(); // Still resolve the promise to prevent navigation issues
    }
  };

  const flushCurrentStepData = async () => {
    try {
      if (flushStepDataFn) {
        await flushStepDataFn();
      }
    } catch (error) {
      console.error('Error flushing step data:', error);
      // Silently handle errors to prevent blocking navigation
    }
  };

  return (
    <ProfileContext.Provider value={{
      currentStep,
      setCurrentStep,
      profileData,
      updateProfileData,
      isLoading,
      lastSaved,
      setFlushStepDataFn,
      flushCurrentStepData,
    }}>
      {children}
    </ProfileContext.Provider>
  );
};

// Custom hook to use the profile context
export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (context === undefined) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }
  return context;
};
